import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import { Palette, Plus, Trash2, ExternalLink, Image as ImageIcon, Sunrise, Save, GripVertical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULT_INDUSTRIAL = {
  theme: 'industrial-hero',
  accent: '#DC2626',
  accent_hover: '#B91C1C',
  tagline: 'MASTER DATA ASSET LIST CONTROL & MASTER MANPOWER DATA PLANNING',
  subtitle: 'Long-Term Equipment & Resource Planning',
  notice: 'Please contact your administrator for your account access',
  hero_images: [],
};

// Apply the theme live to the document so changes are visible immediately after save.
function applyBrandingLive(branding) {
  const root = document.documentElement;
  if (branding && branding.theme === 'industrial-hero') {
    root.setAttribute('data-tenant-theme', 'industrial');
    const accent = branding.accent || '#DC2626';
    const dark = branding.accent_hover || accent;
    root.style.setProperty('--tenant-accent', accent);
    root.style.setProperty('--tenant-accent-dark', dark);
    root.style.setProperty('--tenant-accent-soft', accent + '14');
    root.style.setProperty('--tenant-accent-line', accent + '40');
  } else {
    root.removeAttribute('data-tenant-theme');
    ['--tenant-accent', '--tenant-accent-dark', '--tenant-accent-soft', '--tenant-accent-line']
      .forEach((v) => root.style.removeProperty(v));
  }
}

export default function Branding() {
  const notification = useNotification();
  const { canEdit } = usePermissions();
  const allowed = canEdit('user_management');

  const [form, setForm] = useState({ theme: 'classic', accent: '#DC2626', accent_hover: '#B91C1C', tagline: '', subtitle: '', notice: '', hero_images: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const tenant = (() => { try { return JSON.parse(localStorage.getItem('tenant') || 'null'); } catch { return null; } })();
  const clientId = tenant?.client_id?.toUpperCase() || '';

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branding`, { headers: getAuthHeader() });
      const b = res.data || { theme: 'classic' };
      setForm({
        theme: b.theme || 'classic',
        accent: b.accent || '#DC2626',
        accent_hover: b.accent_hover || '#B91C1C',
        tagline: b.tagline || '',
        subtitle: b.subtitle || '',
        notice: b.notice || '',
        hero_images: Array.isArray(b.hero_images) ? b.hero_images : [],
      });
    } catch {
      notification.error('Failed to load branding');
    } finally {
      setLoading(false);
    }
  }, [notification]);

  useEffect(() => { if (allowed) load(); else setLoading(false); }, [allowed, load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setImage = (idx, val) => setForm((f) => {
    const imgs = [...f.hero_images]; imgs[idx] = val; return { ...f, hero_images: imgs };
  });
  const addImage = () => setForm((f) => ({ ...f, hero_images: [...f.hero_images, ''] }));
  const removeImage = (idx) => setForm((f) => ({ ...f, hero_images: f.hero_images.filter((_, i) => i !== idx) }));

  const applyIndustrialDefaults = () => setForm((f) => ({ ...DEFAULT_INDUSTRIAL, hero_images: f.hero_images.length ? f.hero_images : [] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        theme: form.theme,
        accent: form.accent,
        accent_hover: form.accent_hover,
        tagline: form.tagline,
        subtitle: form.subtitle,
        notice: form.notice,
        hero_images: form.hero_images.map((u) => u.trim()).filter(Boolean),
      };
      const res = await axios.put(`${API}/branding`, payload, { headers: getAuthHeader() });
      const branding = res.data?.branding || payload;
      localStorage.setItem('tenant_branding', JSON.stringify(branding));
      applyBrandingLive(branding);
      notification.success('Branding saved');
    } catch (e) {
      notification.error(e.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return (
      <div className="p-8" data-testid="branding-no-access">
        <h1 className="text-xl font-bold text-slate-900">Branding</h1>
        <p className="mt-2 text-slate-500">You don’t have permission to manage tenant branding.</p>
      </div>
    );
  }

  const isIndustrial = form.theme === 'industrial-hero';
  const previewImg = form.hero_images.find((u) => u && u.trim());

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="branding-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Palette className="w-6 h-6 text-amber-500" /> Tenant Branding
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            White-label the login screen and in-app theme for <span className="font-semibold text-slate-700">{clientId || 'your tenant'}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {clientId && (
            <a href={`/login?client_id=${encodeURIComponent(clientId)}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-amber-600 border border-slate-300 rounded-md px-3 h-9" data-testid="branding-preview-login">
              <ExternalLink className="w-4 h-4" /> Preview login
            </a>
          )}
          <Button onClick={handleSave} disabled={saving} data-testid="branding-save-button" className="bg-amber-500 hover:bg-amber-600 text-white h-9">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Login Theme</Label>
                  <Select value={form.theme} onValueChange={(v) => set('theme', v)}>
                    <SelectTrigger data-testid="branding-theme-select" className="w-full sm:w-72"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="classic">Classic (standard EQUIPR Horizon)</SelectItem>
                      <SelectItem value="industrial-hero">Industrial Hero (branded, red/black)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">
                    {isIndustrial
                      ? 'Full-bleed hero login + a themed in-app accent driven by the colour below.'
                      : 'The standard centered-card login with the default amber brand.'}
                  </p>
                </div>

                {isIndustrial && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                      <ColorField label="Accent Colour" value={form.accent} onChange={(v) => set('accent', v)} testid="branding-accent" />
                      <ColorField label="Accent (hover) Colour" value={form.accent_hover} onChange={(v) => set('accent_hover', v)} testid="branding-accent-hover" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-slate-700">Tagline</Label>
                      <textarea data-testid="branding-tagline" value={form.tagline} onChange={(e) => set('tagline', e.target.value)} rows={2}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm resize-y" placeholder="Big bold hero tagline" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-slate-700">Subtitle</Label>
                      <Input data-testid="branding-subtitle" value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} placeholder="Smaller supporting line" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-slate-700">Notice</Label>
                      <Input data-testid="branding-notice" value={form.notice} onChange={(e) => set('notice', e.target.value)} placeholder="e.g. Please contact your administrator for account access" />
                    </div>
                    <div className="space-y-2 pt-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold text-slate-700">Hero Images</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addImage} data-testid="branding-add-image" className="h-8">
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add image URL
                        </Button>
                      </div>
                      <p className="text-xs text-slate-400">Paste public image URLs (they cross-fade as a slideshow). 3–4 wide landscape photos work best.</p>
                      <div className="space-y-2">
                        {form.hero_images.length === 0 && (
                          <div className="text-xs text-slate-400 border border-dashed border-slate-300 rounded-md px-3 py-4 text-center">No hero images yet — add at least one.</div>
                        )}
                        {form.hero_images.map((url, idx) => (
                          <div key={idx} className="flex items-center gap-2" data-testid={`branding-image-row-${idx}`}>
                            <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
                            <div className="w-14 h-10 rounded border border-slate-200 bg-slate-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                              {url ? <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <ImageIcon className="w-4 h-4 text-slate-300" />}
                            </div>
                            <Input value={url} onChange={(e) => setImage(idx, e.target.value)} placeholder="https://…/image.jpg" className="flex-1 h-9 text-sm" data-testid={`branding-image-input-${idx}`} />
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeImage(idx)} className="h-9 w-9 p-0 text-red-600" data-testid={`branding-remove-image-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="pt-1">
                      <Button type="button" variant="ghost" size="sm" onClick={applyIndustrialDefaults} className="text-xs text-slate-500" data-testid="branding-defaults">
                        Reset text to EQUIPR Horizon defaults
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Live preview */}
          <div className="lg:col-span-1">
            <Card className="overflow-hidden sticky top-6">
              <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Live Preview</p>
              </div>
              <CardContent className="p-0">
                {isIndustrial ? (
                  <div className="relative h-64 bg-black flex flex-col" data-testid="branding-preview">
                    {previewImg && <img src={previewImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" onError={(e) => { e.target.style.display = 'none'; }} />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
                    <div className="relative z-10 p-4 flex items-center gap-2">
                      <div className="w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: form.accent }}>
                        <Sunrise className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-condensed text-lg font-extrabold uppercase text-white leading-none">EQUIPR <span style={{ color: form.accent }}>Horizon</span></span>
                    </div>
                    <div className="relative z-10 mt-auto p-4">
                      <p className="font-condensed font-black uppercase text-white text-xl leading-tight line-clamp-3 drop-shadow">{form.tagline || 'Your hero tagline'}</p>
                      <button className="mt-3 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded" style={{ backgroundColor: form.accent }}>Login</button>
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center bg-slate-100" data-testid="branding-preview">
                    <div className="bg-white rounded-lg shadow px-6 py-5 text-center">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-2">
                        <Sunrise className="w-5 h-5 text-white" />
                      </div>
                      <p className="font-bold text-slate-900 text-sm">EQUIPR Horizon</p>
                      <p className="text-xs text-slate-400 mt-1">Standard login</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-slate-700">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-picker`}
          className="w-10 h-9 rounded border border-slate-300 cursor-pointer p-0.5" />
        <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-hex`} className="h-9 font-mono text-sm uppercase" />
      </div>
    </div>
  );
}
